import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../store';
import {
  PiFileLight,
  PiCloudArrowDownLight,
  PiTrashLight,
  PiEyeLight,
  PiFolderLight,
  PiSpinnerLight,
} from 'react-icons/pi';
import { useGlobalStreaming } from '../../../hooks/useGlobalStreaming';
import { showFeedbackModal } from '../../../store/actions/UserFeedbackActions';
import { showModal, hideModal } from '../../../store/actions/modalActions';
import AttachmentViewer from '../../Tasks/AttachmentViewer';
import FilterBarWithTabs from '../../shared/TailwindComponents/FilterBarWithTabs';
import Table from '../../shared/TailwindComponents/Table';
import { formatDocumentName, formatBytes } from '../../../utils/functions';

interface TeamDocument {
  id: string;
  name: string;
  size: number;
  type: string;
  url: string;
  uploadedBy: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  uploadedAt: string;
}

interface DocumentsTabProps {
  teamId: string;
  documents: TeamDocument[];
  loading?: boolean;
  isUploading?: boolean;
  uploadingCount?: number;
  onUpload?: (files: File[]) => Promise<void>;
  onDelete?: (documentId: string) => Promise<void>;
  onBulkDelete?: (documentIds: string[]) => Promise<void>;
  onRefresh?: () => Promise<void>;
}

const DocumentsTab: React.FC<DocumentsTabProps> = ({
  teamId,
  documents = [],
  loading = false,
  isUploading = false,
  uploadingCount = 0,
  onUpload,
  onDelete,
  onBulkDelete,
  onRefresh,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { viewFile, downloadFile } = useGlobalStreaming();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewingDocId, setViewingDocId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingTotal, setDeletingTotal] = useState(0);

  const handleOpenDocument = async (doc: TeamDocument) => {
    setViewingDocId(doc.id);
    try {
      const result = await viewFile({
        domain: 'teams',
        resourceId: teamId,
        fileId: doc.id,
        filename: doc.name,
        contentType: doc.type,
      });

      if (!result.success || !result.blob) {
        console.error('[handleOpenDocument] Failed to fetch document', result);
        dispatch(showFeedbackModal({
          modalType: 'error',
          message: result.error || t('Failed to load document'),
          showModal: true,
          duration: 3000,
        }));
        setViewingDocId(null);
        return;
      }

      const attachment = {
        AttachmentID: doc.id,
        id: doc.id,
        FileName: doc.name,
        FileType: doc.type,
        ContentType: result.contentType || doc.type,
        size: doc.size,
      };

      const component = (
        <AttachmentViewer
          attachment={attachment}
          blob={result.blob}
          contentType={result.contentType || doc.type}
          filename={result.filename || doc.name}
          onDownload={async () => {
            await downloadFile({
              domain: 'teams',
              resourceId: teamId,
              fileId: doc.id,
              filename: doc.name,
            });
          }}
        />
      );

      dispatch(
        showModal({
          message: doc.name,
          icon: <PiFileLight className='w-6 h-6 text-gray-600' />,
          type: 'attachmentViewer',
          component,
          showModal: true,
        })
      );
    } catch (error) {
      console.error('[handleOpenDocument] Error opening document:', error);
      dispatch(showFeedbackModal({
        modalType: 'error',
        message: t('Error opening document. Please try again.'),
        showModal: true,
        duration: 3000,
      }));
    } finally {
      setViewingDocId(null);
    }
  };

  const handleDownloadDocument = async (doc: TeamDocument) => {
    await downloadFile({
      domain: 'teams',
      resourceId: teamId,
      fileId: doc.id,
      filename: doc.name,
    });
  };

  const handleDeleteDocument = async (documentId: string) => {
    if (!onDelete) return;

    dispatch(showModal({
      type: 'confirmation',
      message: t('Delete Document'),
      subMsg: t('This document will be permanently deleted from the team. This action cannot be undone.'),
      icon: <PiTrashLight className='w-6 h-6 text-red-500' />,
      isDest: true,
      showModal: true,
      onConfirm: async () => {
        setDeletingId(documentId);
        try {
          await onDelete(documentId);
          if (onRefresh) {
            await onRefresh();
          }
          dispatch(showFeedbackModal({
            showModal: true,
            message: t('Document deleted successfully'),
            modalType: 'success',
            duration: 3000,
          }));
        } catch (err) {
          console.error('Error deleting document:', err);
          dispatch(showFeedbackModal({
            showModal: true,
            message: t('Failed to delete document'),
            modalType: 'error',
            duration: 3000,
          }));
        } finally {
          setDeletingId(null);
        }
        dispatch(hideModal());
      },
    }));
  };

  const handleSelectDoc = (id: string) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedIds.length === filteredDocuments.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredDocuments.map(d => d.id));
    }
  };

  const handleDeleteSelected = () => {
    if ((!onBulkDelete && !onDelete) || selectedIds.length === 0) return;

    dispatch(showModal({
      type: 'confirmation',
      message: t('Delete Documents'),
      subMsg: t('{{count}} document(s) will be permanently deleted. This action cannot be undone.', { count: selectedIds.length }),
      icon: <PiTrashLight className='w-6 h-6 text-red-500' />,
      isDest: true,
      showModal: true,
      onConfirm: async () => {
        dispatch(hideModal());
        const toDelete = [...selectedIds];
        setIsDeleting(true);
        setDeletingTotal(toDelete.length);

        try {
          if (onBulkDelete) {
            await onBulkDelete(toDelete);
          } else if (onDelete) {
            for (const docId of toDelete) {
              await onDelete(docId);
            }
          }

          dispatch(showFeedbackModal({
            showModal: true,
            message: t('{{count}} document(s) deleted successfully', { count: toDelete.length }),
            modalType: 'success',
            duration: 3000,
          }));
        } catch (err) {
          console.error('Error deleting documents:', err);
          dispatch(showFeedbackModal({
            showModal: true,
            message: t('Failed to delete documents'),
            modalType: 'error',
            duration: 3000,
          }));
        } finally {
          setIsDeleting(false);
          setDeletingTotal(0);
          setSelectedIds([]);
          if (onRefresh) await onRefresh();
        }
      },
    }));
  };

  const filteredDocuments = useMemo(() =>
    documents.filter(doc =>
      doc.name.toLowerCase().includes(searchQuery.toLowerCase())
    ),
    [documents, searchQuery]
  );

  const tableData = useMemo(() =>
    filteredDocuments.map((doc) => {
      const formatted = formatDocumentName(doc.name);
      return {
        id: doc.id,
        name: formatted.name,
        type: formatted.extension ? formatted.extension.toUpperCase() : '—',
        'Uploaded By': `${doc.uploadedBy.first_name} ${doc.uploadedBy.last_name}`,
        date: new Date(doc.uploadedAt || new Date()).toLocaleDateString('en-GB', {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
        }),
        size: formatBytes(doc.size),
        _doc: doc,
      };
    }),
    [filteredDocuments]
  );

  const tableActions = useMemo(() => [
    {
      label: t('Download'),
      onClick: (row: any) => handleDownloadDocument(row._doc),
      icon: <PiCloudArrowDownLight className="w-4 h-4" />,
    },
    ...(onDelete ? [{
      label: t('Delete'),
      onClick: (row: any) => handleDeleteDocument(row._doc.id),
      icon: <PiTrashLight className="w-4 h-4" />,
      isDestructive: true,
    }] : []),
  ], [onDelete, t]);

  const filterActions = useMemo(() => {
    const acts: Array<{ label: string; onClick: () => void; icon?: React.ReactElement }> = [];
    if ((onBulkDelete || onDelete) && selectedIds.length > 0) {
      acts.push({
        label: t('Delete Selected'),
        onClick: handleDeleteSelected,
        icon: <PiTrashLight className="w-4 h-4" />,
      });
    }
    return acts;
  }, [selectedIds.length, onDelete, t]);

  const isBusy = isUploading || isDeleting;

  return (
    <div className="flex flex-col min-h-0 flex-1">
      <FilterBarWithTabs
        onSearchChange={setSearchQuery}
        noParentPadding={true}
        noParentHeight={true}
        actions={filterActions}
        selectionCount={selectedIds.length}
      />
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="flex-1 min-h-0">
          {isBusy ? (
            <div className="flex flex-col items-center justify-center py-24 gap-3">
              <PiSpinnerLight className={`w-10 h-10 animate-spin ${isDeleting ? 'text-red-500' : 'text-blue-500'}`} />
              <span className={`text-sm font-medium ${isDeleting ? 'text-red-600 dark:text-red-400' : 'text-blue-600 dark:text-blue-400'}`}>
                {isUploading
                  ? t('Uploading {{count}} document(s)...', { count: uploadingCount })
                  : t('Deleting {{count}} document(s)...', { count: deletingTotal })
                }
              </span>
            </div>
          ) : loading ? (
            <div className="space-y-4">
              <div className="h-8 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulseFade"></div>
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulseFade"></div>
                ))}
              </div>
            </div>
          ) : (
            <Table
              columns={['name', 'type', 'Uploaded By', 'date', 'size', 'Actions']}
              data={tableData}
              actions={tableActions}
              enableSelection={!!(onBulkDelete || onDelete)}
              selectedIds={selectedIds}
              onSelect={handleSelectDoc}
              onSelectAll={handleSelectAll}
              renderCell={(column, value, row) => {
                if (column === 'name') {
                  return (
                    <div
                      className="cursor-pointer hover:text-primary-600 dark:hover:text-primary-400 transition-colors flex items-center gap-2"
                      onClick={() => handleOpenDocument(row._doc)}
                    >
                      <PiFileLight className="h-4 w-4 flex-shrink-0 text-gray-400" />
                      <span>{value}</span>
                    </div>
                  );
                }
                if (column === 'type') {
                  return (
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{value}</span>
                  );
                }
                return value;
              }}
              resizableColumns={true}
              defaultColumnWidths={{
                'name': 220,
                'type': 70,
                'Uploaded By': 180,
                'date': 140,
                'size': 100,
                'Actions': 48,
              }}
              total_items={documents.length}
              total_showing_items={filteredDocuments.length}
              setIsShowing={true}
              showingLabel={t('documents')}
              selectedCount={selectedIds.length}
              emptyState={{
                icon: <PiFolderLight className="h-16 w-16" />,
                headline: t('No documents yet'),
                subtext: t('Upload documents to see them listed here'),
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentsTab;
